import {
  ResponsiveContainer, LineChart, Line, AreaChart, Area, BarChart, Bar,
  PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { CHART_COLORS, inr } from "../../lib/format";

const axis = { fontSize: 11, fill: "#94a3b8" };
const grid = "#eef2f7";

function money(v) {
  return "₹" + inr(v);
}

export function AreaTrend({ data, x = "date", y = "value", color = CHART_COLORS[0], height = 240, currency }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ left: 4, right: 8, top: 8, bottom: 0 }}>
        <defs>
          <linearGradient id={`g-${color}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.28} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid stroke={grid} vertical={false} />
        <XAxis dataKey={x} tick={axis} tickLine={false} axisLine={false} minTickGap={28} tickFormatter={(v) => String(v).slice(5)} />
        <YAxis tick={axis} tickLine={false} axisLine={false} width={44} tickFormatter={(v) => (currency ? inr(v) : v)} />
        <Tooltip formatter={(v) => (currency ? money(v) : v.toLocaleString("en-IN"))} labelStyle={{ color: "#334155" }} contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
        <Area type="monotone" dataKey={y} stroke={color} strokeWidth={2} fill={`url(#g-${color})`} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export function MultiLine({ data, x = "date", lines, height = 240, currency }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ left: 4, right: 8, top: 8, bottom: 0 }}>
        <CartesianGrid stroke={grid} vertical={false} />
        <XAxis dataKey={x} tick={axis} tickLine={false} axisLine={false} minTickGap={28} tickFormatter={(v) => String(v).slice(5)} />
        <YAxis tick={axis} tickLine={false} axisLine={false} width={44} tickFormatter={(v) => (currency ? inr(v) : v)} />
        <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        {lines.map((l, i) => (
          <Line key={l.key} type="monotone" dataKey={l.key} name={l.name} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={false} />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

export function Bars({ data, x = "name", y = "value", height = 240, currency, horizontal }) {
  if (horizontal) {
    return (
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} layout="vertical" margin={{ left: 8, right: 16, top: 4, bottom: 4 }}>
          <CartesianGrid stroke={grid} horizontal={false} />
          <XAxis type="number" tick={axis} tickLine={false} axisLine={false} tickFormatter={(v) => (currency ? inr(v) : v)} />
          <YAxis type="category" dataKey={x} tick={axis} tickLine={false} axisLine={false} width={110} />
          <Tooltip formatter={(v) => (currency ? money(v) : v.toLocaleString("en-IN"))} contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
          <Bar dataKey={y} radius={[0, 4, 4, 0]} fill={CHART_COLORS[0]}>
            {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  }
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ left: 4, right: 8, top: 8, bottom: 0 }}>
        <CartesianGrid stroke={grid} vertical={false} />
        <XAxis dataKey={x} tick={axis} tickLine={false} axisLine={false} />
        <YAxis tick={axis} tickLine={false} axisLine={false} width={44} tickFormatter={(v) => (currency ? inr(v) : v)} />
        <Tooltip formatter={(v) => (currency ? money(v) : v.toLocaleString("en-IN"))} contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
        <Bar dataKey={y} radius={[4, 4, 0, 0]} fill={CHART_COLORS[0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

export function Donut({ data, height = 240, currency }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie data={data} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
          {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
        </Pie>
        <Tooltip formatter={(v) => (currency ? money(v) : v.toLocaleString("en-IN"))} contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
      </PieChart>
    </ResponsiveContainer>
  );
}

export function Spark({ data, color = "#6E3667", width = 96, height = 34 }) {
  if (!data || data.length < 2) return <div style={{ width, height }} />;
  const min = Math.min(...data), max = Math.max(...data);
  const rng = max - min || 1;
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * width},${height - ((v - min) / rng) * (height - 4) - 2}`).join(" ");
  return (
    <svg width={width} height={height} className="overflow-visible">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}
